import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { ButtonComponent } from "@components/button/button.component";
import { InputComponent } from "@components/input/input.component";
import { SimpleSelectorComponent as SimpleSelectorComponent_1 , SimpleSelectorComponent } from "@components/simple-selector/simple-selector.component";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { SimpleSelectionItem } from "~app/common/components/selector/simple-selection-item";

import { AvtalService } from "~app/features/virkeskop/services/avtal.service";
import { TradslagMediatorService } from "~app/features/virkeskop/services/tradslag-mediator.service";

@Component({
    selector: "rask-tradslag-mall",
    templateUrl: "./tradslag-mall.component.html",
    styleUrls: ["./tradslag-mall.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SimpleSelectorComponent_1,
        ActionButtonComponent,
        InputComponent,
        FormsModule,
        ButtonComponent
    ]
})
export class TradslagMallComponent implements OnInit, OnDestroy {
    @Input() kontraktReadonly = false;
    @ViewChild("mallSelector") mallSelector: SimpleSelectorComponent;
    @ViewChild("saveMallPanel") saveMallPanel: ElementRef;

    protected aktivmall: SimpleSelectionItem;
    protected bestandsmallar: SimpleSelectionItem[];
    protected mallNamn: string;
    protected showPanel = false;

    private readonly avtalService = inject(AvtalService);
    private readonly cdRef = inject(ChangeDetectorRef);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly tradslagMediatorService = inject(TradslagMediatorService);
    private readonly zone = inject(NgZone);

    clickout = (event: any) => {
        if (!this.showPanel) {
            return;
        }

        if (!this.saveMallPanel.nativeElement.contains(event.target)) {
            this.closeSavepopup();
            this.cdRef.detectChanges();
        }
    };

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        document.removeEventListener("click", this.clickout);
    }

    ngOnInit(): void {
        this.zone.runOutsideAngular(() => {
            document.addEventListener("click", this.clickout);
        });

        this.loadMallar();

        this.tradslagMediatorService.onMallChanged$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.loadMallar();
            });

        this.tradslagMediatorService.onKalkylMetodChanged$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                if (this.mallSelector) {
                    this.mallSelector.clearSelection();
                }
            });
    }

    protected canSave(): boolean {
        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (this.mallNamn) {
            return true;
        }
        return false;
    }

    protected close() {
        this.showPanel = false;
        this.mallNamn = "";
    }

    protected closeSavepopup() {
        this.close();
    }

    protected loadMallar(): void {
        this.avtalService.getAllSnabbKalkylmallar()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: allBestandsmallar => {
                    this.bestandsmallar = allBestandsmallar.map((value) => ({
                        id: value.id,
                        text: value.name,
                        value: value.id
                    }));
                },
                error: () => {
                    this.bestandsmallar = [];
                }
            });
    }

    protected mallSelected(selectedItem: SimpleSelectionItem): void {
        if (!selectedItem || !selectedItem.value || selectedItem.value === this.aktivmall?.value) {
            return;
        }
        this.tradslagMediatorService.loadMall(selectedItem.value);
        this.aktivmall = selectedItem.value;
    }

    protected openSavepopup() {
        setTimeout(() => {
            this.showPanel = true;
        }, 100);
    }

    protected tryDeleteMall(item: SimpleSelectionItem): void {
        this.tradslagMediatorService.deleteMall(item);
    }

    protected trySaveMall(event) {
        event.preventDefault();
        if (this.mallNamn) {
            this.tradslagMediatorService.saveMall(this.mallNamn);
            this.closeSavepopup();
        }
    }

    protected viewFormatterMallar = (i: SimpleSelectionItem) => i.text;
}
